/** Settings · Appointment & booking (spec 15 §8, §10) — data shapes. */

import type { Role } from '../settings-practice/types';

/** Eight fixed categories, each with the colour the board gives its code chip. */
export type ReasonCategory =
  | 'diagnostic'
  | 'emergency'
  | 'hygiene'
  | 'periodontic'
  | 'restorative'
  | 'endodontic'
  | 'surgical'
  | 'orthodontic';

export const REASON_CATEGORIES: { id: ReasonCategory; label: string; colour: string }[] = [
  { id: 'diagnostic', label: 'Diagnostic & Exam', colour: '#4C5BD4' },
  { id: 'emergency', label: 'Emergency', colour: '#BE4440' },
  { id: 'hygiene', label: 'Hygiene & Preventive', colour: '#2E7D62' },
  { id: 'periodontic', label: 'Periodontic', colour: '#20968C' },
  { id: 'restorative', label: 'Restorative', colour: '#A9833A' },
  { id: 'endodontic', label: 'Endodontic', colour: '#4A3B7A' },
  { id: 'surgical', label: 'Surgical', colour: '#9B3D6E' },
  { id: 'orthodontic', label: 'Orthodontic', colour: '#5B3F8C' },
];

export const CATEGORY_LABEL: Record<ReasonCategory, string> = REASON_CATEGORIES.reduce(
  (a, c) => ({ ...a, [c.id]: c.label }),
  {} as Record<ReasonCategory, string>,
);
export const CATEGORY_COLOUR: Record<ReasonCategory, string> = REASON_CATEGORIES.reduce(
  (a, c) => ({ ...a, [c.id]: c.colour }),
  {} as Record<ReasonCategory, string>,
);

/**
 * Three states, because the built app's two-state `Online / Internal` never says
 * whether ELVA may book on the phone — the only question that matters (§8.1).
 */
export type BookableBy = 'anyone' | 'elva_and_staff' | 'staff_only';

export const BOOKABLE_LABEL: Record<BookableBy, string> = {
  anyone: 'Anyone',
  elva_and_staff: 'ELVA & staff',
  staff_only: 'Staff only',
};

export interface AppointmentReason {
  id: string;
  code: string;
  displayName: string;
  category: ReasonCategory;
  durationMinutes: number;
  bufferAfterMinutes: number;
  /** "Next open slot", or a wait before ELVA may offer it. New on this page. */
  earliestBookable: string;
  /** May include roles nobody holds — kept, not deleted, so hiring changes it (§8.1). */
  roles: Role[];
  bookableBy: BookableBy;
  cdtCodes: string[];
  patientDescription?: string;
  /** Read AFTER the sentences. */
  elvaDescription?: string;
  bookable: boolean;
  /** Online booking (spec 50 §2.1) — who the reason is offered to online. Only
   * meaningful when `bookableBy === 'anyone'`; an Emergency reason is never online. */
  onlineForNew?: boolean;
  onlineForReturning?: boolean;
}

export type SentenceSource = 'written_here' | 'confirmed_from_call' | 'corrected_from_call';

export const SENTENCE_SOURCE_LABEL: Record<SentenceSource, string> = {
  written_here: 'written here',
  confirmed_from_call: 'confirmed from a call',
  corrected_from_call: 'corrected from a call',
};

export interface ReasonSentence {
  id: string;
  reasonId: string;
  /** What a patient would actually say, not the clinical phrasing. */
  text: string;
  /** The optional "only when" — may refer only to what ELVA can see (§8.4). */
  condition?: string;
  source: SentenceSource;
  author: string;
}

export interface ReasonsData {
  reasons: AppointmentReason[];
  sentences: ReasonSentence[];
  /** Close-call bookings waiting on Train ELVA — the count and a link, not the rows. */
  waitingFromCalls: number;
  waitingCloseCall: number;
  waitingNothingNear: number;
}

/* ───────────────────── Calendar insights (§10) ──────────────────────────── */

export type InsightView = 'daily' | 'weekly' | 'monthly';
export type InsightGroup = 'revenue' | 'retention' | 'clinical' | 'financial';

export const INSIGHT_GROUPS: { id: InsightGroup; label: string; colour: string }[] = [
  { id: 'revenue', label: 'Revenue & schedule', colour: '#4C5BD4' },
  { id: 'retention', label: 'Patient retention & growth', colour: '#BE4440' },
  { id: 'clinical', label: 'Clinical risk & operations', colour: '#A9833A' },
  { id: 'financial', label: 'Financial & revenue cycle', colour: '#2E7D62' },
];

/**
 * The three views do not share a check list — some checks exist in only one.
 * This is why it is three sections and not one table with three columns (§10).
 */
export interface InsightCheck {
  id: string;
  view: InsightView;
  group: InsightGroup;
  label: string;
  enabled: boolean;
}
